import React, { useEffect, useRef, useState } from 'react';

import { AnthropicModel, fetchAnthropicModels } from './api/anthropicClient';

interface ApiKeyModalProps {
  onCancel: () => void;
  onComplete: (result: { apiKey: string; models: AnthropicModel[]; selectedModelId: string }) => void;
}

type Phase =
  | { kind: 'enter-key'; error?: string }
  | { kind: 'validating' }
  | { kind: 'select-model'; models: AnthropicModel[]; selectedId: string };

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({ onCancel, onComplete }) => {
  const [apiKey, setApiKey] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'enter-key' });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const validate = async () => {
    const key = apiKey.trim();
    if (!key) {
      setPhase({ kind: 'enter-key', error: 'Please enter an API key.' });
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setPhase({ kind: 'validating' });

    try {
      const models = await fetchAnthropicModels(key);
      if (abortRef.current?.signal.aborted) return;

      if (models.length === 0) {
        setPhase({ kind: 'enter-key', error: 'No models are available for this API key.' });
        return;
      }
      setPhase({ kind: 'select-model', models, selectedId: models[0].id });
    } catch (err) {
      if (abortRef.current?.signal.aborted) return;
      setPhase({
        kind: 'enter-key',
        error: err instanceof Error ? err.message : 'Unable to validate the API key.',
      });
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setApiKey('');
    onCancel();
  };

  const handleConfirm = () => {
    if (phase.kind !== 'select-model') return;
    onComplete({ apiKey: apiKey.trim(), models: phase.models, selectedModelId: phase.selectedId });
    setApiKey('');
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{phase.kind === 'select-model' ? 'Choose a model' : 'Add your Anthropic API key'}</h2>

        {phase.kind === 'enter-key' && (
          <EnterKeyPhase
            apiKey={apiKey}
            error={phase.error}
            onChange={setApiKey}
            onSubmit={validate}
            onCancel={handleCancel}
          />
        )}

        {phase.kind === 'validating' && <ValidatingPhase />}

        {phase.kind === 'select-model' && (
          <SelectModelPhase
            models={phase.models}
            selectedId={phase.selectedId}
            onSelect={(id) => setPhase({ ...phase, selectedId: id })}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        )}
      </div>
    </div>
  );
};

interface EnterKeyPhaseProps {
  apiKey: string;
  error?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const EnterKeyPhase: React.FC<EnterKeyPhaseProps> = ({ apiKey, error, onChange, onSubmit, onCancel }) => (
  <>
    <ul style={{ paddingLeft: '1.2em', margin: '0 0 1rem 0', color: '#444', fontSize: '0.9rem' }}>
      <li>Your key is only stored in memory. It is forgotten as soon as you refresh the page.</li>
      <li>Your key is only sent to Anthropic, never to our servers.</li>
    </ul>
    <input
      type="password"
      placeholder="sk-ant-..."
      className="modal-input"
      value={apiKey}
      autoFocus
      autoComplete="off"
      spellCheck={false}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSubmit();
      }}
    />
    {error && <div className="error-message">{error}</div>}
    <div className="modal-buttons">
      <button className="modal-button cancel" onClick={onCancel}>
        Cancel
      </button>
      <button className="modal-button submit" onClick={onSubmit} disabled={!apiKey.trim()}>
        Continue
      </button>
    </div>
  </>
);

const ValidatingPhase: React.FC = () => (
  <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#444' }}>
    Validating key and fetching available models…
  </div>
);

interface SelectModelPhaseProps {
  models: AnthropicModel[];
  selectedId: string;
  onSelect: (id: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const SelectModelPhase: React.FC<SelectModelPhaseProps> = ({
  models,
  selectedId,
  onSelect,
  onConfirm,
  onCancel,
}) => (
  <>
    <p style={{ color: '#444', marginTop: 0 }}>
      Pick the model you want to use now. All listed models will be added to your model dropdown.
    </p>
    <div
      style={{
        maxHeight: '260px',
        overflowY: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        marginBottom: '1rem',
      }}
    >
      {models.map((m) => (
        <label
          key={m.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.6rem 0.8rem',
            borderBottom: '1px solid #f0f0f0',
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name="anthropic-model"
            value={m.id}
            checked={selectedId === m.id}
            onChange={() => onSelect(m.id)}
            style={{ marginRight: '0.6rem' }}
          />
          <span>
            <div style={{ fontWeight: 500 }}>{m.displayName}</div>
            <div style={{ fontSize: '0.8rem', color: '#888' }}>{m.id}</div>
          </span>
        </label>
      ))}
    </div>
    <div className="modal-buttons">
      <button className="modal-button cancel" onClick={onCancel}>
        Cancel
      </button>
      <button className="modal-button submit" onClick={onConfirm}>
        Use this model
      </button>
    </div>
  </>
);

export default ApiKeyModal;
